import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import usePageTitle from 'hooks/usePageTitle';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { InfoTooltip, Tabs } from 'DesignSystem';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import React, { FC, PropsWithChildren } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { TRouteParamsWithId } from 'types/router.types';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

enum EPartnerComponentChainTabKey {
  COMPONENTS = 'components',
  CHAINS = 'component-chains',
}

interface IPartnerComponentsLayoutProps extends PropsWithChildren {
  selectedTab: `${EPartnerComponentChainTabKey}`;
}

const COMPONENT_TABS: ITab<EPartnerComponentChainTabKey>[] = [
  {
    key: EPartnerComponentChainTabKey.CHAINS,
    label: 'Component chains',
    startIconName: 'component-chain',
  },
  {
    key: EPartnerComponentChainTabKey.COMPONENTS,
    label: 'Unique components',
    startIconName: 'components',
  },
];

const PartnerComponentsLayout: FC<IPartnerComponentsLayoutProps> = ({ children, selectedTab }) => {
  usePageTitle('Partner component chains');
  const { id: companyId } = useParams<TRouteParamsWithId>();
  const navigate = useNavigate();
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      variables: {
        id: companyId,
      },
      skip: !companyId,
      fetchPolicy: 'cache-first',
    }
  );

  const handleChangeTab = (tab: EPartnerComponentChainTabKey) => {
    if (tab === EPartnerComponentChainTabKey.COMPONENTS) {
      navigate(`/partners/${companyId}/components`);
    } else {
      navigate(`/partners/${companyId}/component-chains`);
    }
  };

  return (
    <PageContainer>
      <Box>
        <PageTitle
          title="Partner component chains"
          titleExtension={partnerData?.partner?.name}
          goBackLabel="All partners"
          goBackUrl={`/partners`}
          TitleAdornment={
            <Box ml={1}>
              <InfoTooltip
                size={EAvailableSizes.LARGE}
                text="Components are ingredients, packaging or something else that makes up your end products. All components that are part of your supply chains appear here. You can specify what components are coming from what partner and re-use your components in several supply chain activities. Component chains are re-usable sub-supply chains. They describe the material flow of your components, and can also be re-used across several of your different product’s supply chains."
              />
            </Box>
          }
        />
      </Box>

      {!isPartner && (
        <Box mt={3} mb={1}>
          <Tabs
            tabs={COMPONENT_TABS}
            selectedTab={selectedTab as EPartnerComponentChainTabKey}
            onChange={handleChangeTab}
          />
        </Box>
      )}

      {children}
    </PageContainer>
  );
};

export default PartnerComponentsLayout;
